import { hasComposition } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import { keyframes, styled } from '../../../lib/stitches'
import Avatar from '../../core/Avatar'
import Button from '../../core/Button/Button'
import { Column as VerticalColumn, IconButton } from '../../core/Components'
import FavoriteButton from '../../core/FavoriteButton'
import * as Icons from '../../core/icons'
import { CheckIcon, LinkIcon, PlusIcon } from '../../core/icons/icons'
import { MediaTypes, MediaTypesType } from '../../core/Utils'
import { gray3 } from '../../platform/color'
import useStemPreviewRow from '../hooks/useStemPreviewRow'
import NewStemVisualizer from './NewStemVisualizer'
import { Column } from './StemTable'

interface StemRowProps {
  stem: IArpMedia
  index?: number
  color?: string
}

export const Container = styled('div', {
  paddingTop: '12px',
  paddingBottom: '12px',
  variants: {
    disabled: {
      true: { opacity: 0.3 }
    }
  }
})

export const Row = styled('div', {
  marginBottom: '$10',
  display: 'flex',
  height: '50px'
})

export const Tag = styled('span', {
  cursor: 'pointer',
  userSelect: 'none',
  paddingLeft: '5px',
  paddingRight: '5px',
  borderRadius: '9999px',
  fontSize: '$12',
  lineHeight: '$20',
  background: 'rgba(0,0,0,.05)',
  '&:hover': { background: 'rgba(0,0,0,.1)' },
  '&:active': { background: 'rgba(0,0,0,.15)' }
})

export const Text = styled('span', {
  fontSize: '$12',
  lineHeight: '25px',
  fontWeight: '$medium',
  position: 'relative',
  variants: {
    big: {
      true: { fontSize: '$14' }
    },
    bold: {
      true: { fontWeight: '$semibold' }
    },
    secondary: {
      true: { opacity: 0.6 }
    },
    clickable: {
      true: {
        cursor: 'pointer',
        userSelect: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        '&:hover': {
          opacity: 1
        }
      }
    },
    muted: {
      true: {
        color: '$gray13'
      }
    }
  }
})

type PlayPauseButtonProps = { isPlaying?: boolean } & ComponentProps<typeof IconButton>
export const PlayPauseButton = ({ isPlaying = false, ...props }: PlayPauseButtonProps) => (
  <IconButton {...props} css={{ marginRight: '8px' }}>
    {isPlaying ? <Icons.StopIcon /> : <Icons.PlayIcon />}
  </IconButton>
)

export const StyledLinkIcon = styled(LinkIcon, { marginLeft: '5px' })
export const StyledCheckIcon = styled(CheckIcon, { marginRight: '2px' })
export const StyledPlusIcon = styled(PlusIcon, { marginRight: '2px' })

type AddButtonProps = ComponentProps<typeof Button> & { isAdded?: boolean }
export const AddButton: FC<AddButtonProps> = ({ isAdded, ...props }) => {
  const icon = isAdded ? <StyledCheckIcon /> : <StyledPlusIcon />
  const label = isAdded ? 'Added' : 'Add to song'
  return (
    <Button type={isAdded ? 'success' : 'primary'} {...props}>
      {icon}
      {label}
    </Button>
  )
}

type DetailsButtonProps = { url: string; hideText?: boolean }
export const DetailsButton: FC<DetailsButtonProps> = ({ url, hideText }) => {
  const navigate = useNavigate()
  return (
    <Button onClick={() => navigate(url)} css={{ padding: hideText && '5px' }} title="Details">
      {!hideText && 'Details'}
      <StyledLinkIcon />
    </Button>
  )
}

export const sanitizeString = (rawString?: string) =>
  rawString && rawString.length > 0 ? rawString : '—'
export const sanitizeStringArray = (rawArray?: string[]) =>
  rawArray && rawArray.length > 0 ? rawArray : ['None']

const StemRow: FC<StemRowProps> = ({ stem, color }) => {
  const {
    artistProfile,
    samples,
    sampleName,
    handleAddButtonClick,
    isAdded,
    rowColor,
    arp,
    selectedLoopIndex,
    setSelectedLoopIndex
  } = useStemPreviewRow(stem, color)

  return (
    <Container>
      <Row>
        {/* Title and Artist */}
        <Column width={2}>
          <Avatar
            imageUrl={artistProfile?.profile_picture}
            css={{ marginRight: '14px', minWidth: '40px' }}
          />
          {/* <PlayPauseButton isPlaying={isPlaying && isCurrentlyPlaying} onClick={handlePlayButton} /> */}
          <VerticalColumn
            css={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            <Link
              to={`/stem?token=${stem.id}`}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              <Text big bold className="underline-on-hover">
                {stem.metadata.title}
              </Text>
            </Link>
            <Link to={`/profile/${stem.user.walletAddress}`}>
              <Text big bold secondary clickable className="underline-on-hover">
                {sanitizeString(stem.user.displayName)}
              </Text>
            </Link>
          </VerticalColumn>
        </Column>
        {/* Loops */}
        <Column width={2}>
          {
            hasComposition(stem.asInternalArpMedia()) ? (
              <NewStemVisualizer stem={stem} css={{ width: '100%' }} />
            ) : null
            //  ( // This has to be null for some reason.
            //   <SampleVisualizer sample={stem} />
            // )
          }
        </Column>
        {/* Sample */}
        <Column width={1}>
          <Text muted>
            {samples.length === 1 ? (
              <Link to={`/sample?token=${samples[0]}`} className="underline-on-hover">
                {sanitizeString(sampleName)}
              </Link>
            ) : (
              sanitizeString(sampleName)
            )}
          </Text>
        </Column>
        {/* Remix Count */}
        <Column width={1}>
          <Text muted>{stem.remixCount}</Text>
        </Column>
        {/* Controls */}
        <Column width={1} css={{ justifyContent: 'end', overflow: 'visible' }}>
          <AddButton onClick={handleAddButtonClick} isAdded={isAdded} css={{ width: '116px' }} />
          <FavoriteButton arpMediaIndex={stem.id} mediaType={MediaTypes.STEM as MediaTypesType} />
        </Column>
      </Row>
    </Container>
  )
}

const skeletonAnimation = keyframes({
  '0%': { opacity: 1 },
  '50%': { opacity: 0.3 },
  '100%': { opacity: 1 }
})

export const Skeleton = styled('div', {
  borderRadius: '6px',
  background: gray3,
  boxSizing: 'border-box',
  animation: `linear 2s infinite ${skeletonAnimation}`
})

export const StemRowSkeleton = () => (
  <Container>
    <Row>
      <Column width={2}>
        <Skeleton css={{ height: '25px', marginRight: '32px', width: '100%' }} />
      </Column>
      <Column width={1}>
        <Skeleton css={{ height: '25px', marginRight: '32px', width: '100%' }} />
      </Column>
      <Column width={1}>
        <Skeleton css={{ height: '25px', marginRight: '32px', width: '100%' }} />
      </Column>
      <Column width={1}>
        <Skeleton css={{ height: '25px', marginRight: '32px', width: '100%' }} />
      </Column>
      <Column width={1}>
        <Skeleton css={{ height: '25px', marginRight: '32px', width: '100%' }} />
      </Column>
      <Column width={1}>
        <Skeleton css={{ height: '25px', marginRight: '32px', width: '100%' }} />
      </Column>
      <Column width={1}>
        <Skeleton css={{ height: '25px', marginLeft: '32px', width: '100%' }} />
      </Column>
    </Row>
    <Skeleton css={{ height: '38px', width: '100%' }} />
  </Container>
)

export default observer(StemRow)
